import axios from 'axios';
import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Wallet, Plus, Trash2, Settings, TrendingUp, TrendingDown, Edit3, Search,
  DollarSign, Calendar, Loader2, Save, ArrowUpCircle, ArrowDownCircle,
  Building2, User, Users, HardHat, ChevronDown, BarChart3, CheckCircle
} from "lucide-react";
import { formatCurrency } from "@/lib/config";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";

// Mapping catégorie revenu → département
const REVENUE_CATEGORY_DEPT_MAP = {
  "contrat_peinture": "PRQC",
  "contrat_location": "Location",
  "contrat_lmds": "LMDS"
};

const REVENUE_CATEGORIES = [
  { value: "contrat_peinture", label: "Contrat Peinture" },
  { value: "contrat_location", label: "Contrat Location" },
  { value: "contrat_lmds", label: "Contrat LMDS" }
];

const EXPENSE_CATEGORIES = [
  { value: "salaire", label: "Salaire", needsClassification: true },
  { value: "prime", label: "Prime", needsClassification: true },
  { value: "bonus", label: "Bonus", needsClassification: true },  // 🆕 2026-05-12
  { value: "dividendes", label: "Dividendes" },
  { value: "immobilisation", label: "Dépenses en immobilisation", hasSub: true }
];

const IMMOBILISATION_SUB_CATEGORIES = [
  { value: "outillage", label: "Outillage" },
  { value: "materiel_roulant", label: "Matériel Roulant" },
  { value: "immobilier_amenagements", label: "Immobilier et Aménagements" },
  { value: "technologie_numerique", label: "Technologie et numérique" },
  { value: "formation_capital_humain", label: "Formation et capital humain" },
  { value: "investissement_croissance", label: "Investissement en croissance" },
  { value: "securite_conformite", label: "Sécurité en conformité" },
  { value: "investissements_financiers", label: "Investissements financiers" }
];

const DEPARTMENTS = ["PRQC", "LMDS", "Location", "GLOBAL"];

const DEFAULT_TX = {
  date: new Date().toISOString().split('T')[0],
  type: "revenue",
  amount: "",
  description: "",
  categorie: "",
  sous_categorie: "",
  departement: "PRQC",
  client_name: "",
  salary_type: "terrain",
  admin_ventilation: { PRQC: 100, LMDS: 0, Location: 0 },
  employee_id: ""
};

// ---- Autocomplete Client Component ----
const ClientAutocomplete = ({ value, onChange, clients }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(value || "");
  const ref = useRef(null);

  useEffect(() => { setSearch(value || ""); }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = clients.filter(c =>
    c.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={ref} className="relative" data-testid="client-autocomplete">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Saisir ou sélectionner un client..."
          className="pl-9"
          data-testid="client-name-input"
        />
        {clients.length > 0 && (
          <ChevronDown
            className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 cursor-pointer"
            onClick={() => setOpen(!open)}
          />
        )}
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-48 overflow-auto rounded-md border bg-white shadow-lg">
          {filtered.map((c) => (
            <div
              key={c}
              className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-100 ${c === value ? 'bg-slate-50 font-medium' : ''}`}
              onClick={() => { onChange(c); setSearch(c); setOpen(false); }}
              data-testid={`client-option-${c}`}
            >
              {c}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const GestionCash = () => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({ initial_balance: 0, start_date: "" });
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [cashClients, setCashClients] = useState([]);
  const [adminEmployees, setAdminEmployees] = useState([]);
  const [terrainEmployees, setTerrainEmployees] = useState([]);
  const [breakdown, setBreakdown] = useState({ expense_by_category: {}, revenue_by_category: {}, revenue_by_department: {} });

  // Dialog states
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  // 🆕 2026-05-12 — Modal de détection des doublons cash ↔ paiements_cash des punchs
  const [duplicatesModal, setDuplicatesModal] = useState({ open: false, duplicates: [], loading: false, selected: new Set() });
  const [editConfig, setEditConfig] = useState({ initial_balance: 0, start_date: "" });
  const [newTransaction, setNewTransaction] = useState({ ...DEFAULT_TX });

  useEffect(() => { loadData(); }, [year]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configRes, balanceRes, txRes, clientsRes, empRes, terrainRes, breakdownRes] = await Promise.all([
        axios.get(`${api.baseUrl}/cash/config`),
        axios.get(`${api.baseUrl}/cash/balance`),
        axios.get(`${api.baseUrl}/cash/transactions?year=${year}`),
        axios.get(`${api.baseUrl}/cash/clients-cash`),
        axios.get(`${api.baseUrl}/cash/employees-admin`),
        axios.get(`${api.baseUrl}/employees?actifs=true`),
        axios.get(`${api.baseUrl}/cash/breakdown?year=${year}`)
      ]);
      const configData = configRes.data;
      const balanceData = balanceRes.data;
      const txData = txRes.data;
      const clientsData = clientsRes.data;
      const empData = empRes.data;
      const terrainData = terrainRes.data;
      const breakdownData = breakdownRes.data;

      setConfig(configData);
      setEditConfig(configData);
      setBalance(balanceData);
      setTransactions(txData.transactions || []);
      setCashClients(clientsData.clients || []);
      setAdminEmployees(empData.employees || []);
      // Employés terrain = peintre, chef_equipe, installateur (exclure admins déjà listés)
      const terrains = (terrainData.employees || terrainData || []).filter(e =>
        ['peintre', 'chef_equipe', 'installateur'].includes(e.role) && e.est_actif !== false
      ).map(e => ({
        id: e.id,
        nom_complet: `${e.prenom || ''} ${e.nom || ''}`.trim(),
        role: e.role,
      }));
      setTerrainEmployees(terrains);
      setBreakdown(breakdownData);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      const response = await axios.post(`${api.baseUrl}/cash/config`, editConfig);
      if (response.status === 200) {
        toast.success("Configuration sauvegardée");
        setIsConfigOpen(false);
        loadData();
      }
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleCategoryChange = useCallback((categorie, isRevenue) => {
    const updates = { categorie, sous_categorie: "" };
    if (isRevenue && REVENUE_CATEGORY_DEPT_MAP[categorie]) {
      updates.departement = REVENUE_CATEGORY_DEPT_MAP[categorie];
    }
    setNewTransaction(prev => ({ ...prev, ...updates }));
  }, []);

  const addTransaction = async () => {
    if (!newTransaction.amount || !newTransaction.description) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    const needsClassification = EXPENSE_CATEGORIES.find(
      c => c.value === newTransaction.categorie && c.needsClassification
    );

    try {
      const payload = {
        ...newTransaction,
        amount: parseFloat(newTransaction.amount)
      };

      // Build combined category for immobilisation
      if (newTransaction.categorie === "immobilisation" && newTransaction.sous_categorie) {
        payload.categorie = `immobilisation:${newTransaction.sous_categorie}`;
      }

      if (needsClassification && newTransaction.type === "expense") {
        payload.salary_type = newTransaction.salary_type;
        if (newTransaction.salary_type === "admin") {
          payload.admin_ventilation = newTransaction.admin_ventilation;
        }
        // Associer l'employé terrain si sélectionné (pour apparition dans Mon Espace)
        if (newTransaction.salary_type === "terrain" && newTransaction.employee_id) {
          payload.employee_id = newTransaction.employee_id;
          const emp = terrainEmployees.find(e => e.id === newTransaction.employee_id);
          if (emp) payload.employee_nom = emp.nom_complet;
        }
      }

      const response = await axios.post(`${api.baseUrl}/cash/transactions`, payload);

      if (response.status === 200) {
        toast.success(payload.id ? "Transaction modifiée" : "Transaction ajoutée");
        setIsAddOpen(false);
        setNewTransaction({ ...DEFAULT_TX });
        loadData();
      }
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  // 🆕 2026-05-12 — Ouvre le dialog en mode édition d'une transaction existante.
  //   Le POST /cash/transactions est en upsert sur l'`id` → réutilise le même endpoint.
  const openEditDialog = (tx) => {
    let cat = tx.categorie || "";
    let sub = tx.sous_categorie || "";
    if (cat.startsWith("immobilisation:")) {
      const parts = cat.split(":");
      cat = "immobilisation";
      sub = parts[1] || "";
    }
    setNewTransaction({
      id: tx.id,
      type: tx.type || "revenue",
      amount: String(tx.amount || ""),
      description: tx.description || "",
      date: tx.date || new Date().toISOString().split("T")[0],
      client_name: tx.client_name || "",
      departement: tx.departement || "",
      categorie: cat,
      sous_categorie: sub,
      salary_type: tx.salary_type || "",
      admin_ventilation: tx.admin_ventilation || {},
      employee_id: tx.employee_id || "",
    });
    setIsAddOpen(true);
  };

  const deleteTransaction = async (id) => {
    try {
      const response = await axios.delete(`${api.baseUrl}/cash/transactions/${id}`);
      if (response.status === 200) {
        toast.success("Transaction supprimée");
        loadData();
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // 🆕 2026-05-12 — Détecter les doublons cash ↔ paiements_cash des punchs.
  const detectDuplicates = async () => {
    setDuplicatesModal({ open: true, duplicates: [], loading: true, selected: new Set() });
    try {
      const r = await axios.get(`${api.baseUrl}/cash/transactions/duplicates-with-punches`);
      const dups = r.data?.duplicates || [];
      setDuplicatesModal({
        open: true,
        loading: false,
        duplicates: dups,
        selected: new Set(dups.map(d => d.transaction_id)),  // tout sélectionné par défaut
      });
    } catch (e) {
      toast.error("Erreur lors de la détection des doublons");
      setDuplicatesModal({ open: false, duplicates: [], loading: false, selected: new Set() });
    }
  };

  const toggleDuplicateSelection = (txId) => {
    setDuplicatesModal(m => {
      const s = new Set(m.selected);
      if (s.has(txId)) s.delete(txId); else s.add(txId);
      return { ...m, selected: s };
    });
  };

  const confirmDeleteDuplicates = async () => {
    const ids = Array.from(duplicatesModal.selected);
    if (!ids.length) {
      toast.error("Aucune transaction sélectionnée");
      return;
    }
    try {
      await axios.post(`${api.baseUrl}/cash/transactions/duplicates-with-punches/delete`, { transaction_ids: ids });
      toast.success(`${ids.length} doublon${ids.length > 1 ? 's' : ''} supprimé${ids.length > 1 ? 's' : ''}`);
      setDuplicatesModal({ open: false, duplicates: [], loading: false, selected: new Set() });
      loadData();
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const revenues = transactions.filter(t => t.type === "revenue");
  const expenses = transactions.filter(t => t.type === "expense");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const isRevenue = newTransaction.type === "revenue";
  const isExpense = newTransaction.type === "expense";
  const isSalaryOrPrime = isExpense && (newTransaction.categorie === "salaire" || newTransaction.categorie === "prime" || newTransaction.categorie === "bonus");
  const isImmobilisation = isExpense && newTransaction.categorie === "immobilisation";
  const isAdminSalary = isSalaryOrPrime && newTransaction.salary_type === "admin";

  return (
    <div className="space-y-6" data-testid="gestion-cash-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Argent Cash & Dépenses</h1>
          <p className="text-slate-500">Gestion des revenus et dépenses en argent comptant</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-28" data-testid="year-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2026">2026</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => setIsConfigOpen(true)} data-testid="config-btn">
            <Settings className="w-4 h-4 mr-2" />
            Config
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={detectDuplicates}
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="detect-duplicates-btn"
            title="Identifie les transactions cash (salaire/prime) qui doublonnent avec les paiements cash des punchs."
          >
            <Search className="w-4 h-4 mr-2" />
            Détecter doublons
          </Button>
          <Button onClick={() => setIsAddOpen(true)} data-testid="add-transaction-btn">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter
          </Button>
        </div>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-lg"><Wallet className="w-6 h-6" /></div>
              <div>
                <p className="text-sm text-emerald-100">Solde actuel</p>
                <p className="text-2xl font-bold" data-testid="current-balance">
                  {formatCurrency(balance?.current_balance || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg"><Calendar className="w-6 h-6 text-blue-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Solde initial</p>
                <p className="text-xl font-bold text-slate-900" data-testid="initial-balance">
                  {formatCurrency(balance?.initial_balance || 0)}
                </p>
                <p className="text-xs text-slate-400">{config.start_date || "Non configuré"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg"><ArrowUpCircle className="w-6 h-6 text-emerald-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Revenus {year}</p>
                <p className="text-xl font-bold text-emerald-600" data-testid="total-revenue">
                  +{formatCurrency(balance?.total_revenue || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-lg"><ArrowDownCircle className="w-6 h-6 text-red-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Dépenses {year}</p>
                <p className="text-xl font-bold text-red-600" data-testid="total-expense">
                  -{formatCurrency(balance?.total_expense || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alert if not configured */}
      {!config.start_date && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Settings className="w-8 h-8 text-amber-500 shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-amber-900">Configuration requise</p>
                <p className="text-sm text-amber-700">
                  Définissez votre solde initial et la date de début pour commencer.
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setIsConfigOpen(true)}>Configurer</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <CashCharts breakdown={breakdown} year={year} />

      {/* Transactions Tabs */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">
            Toutes ({transactions.length})
          </TabsTrigger>
          <TabsTrigger value="revenues" data-testid="tab-revenues">
            <ArrowUpCircle className="w-4 h-4 mr-1" />
            Revenus ({revenues.length})
          </TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">
            <ArrowDownCircle className="w-4 h-4 mr-1" />
            Dépenses ({expenses.length})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="all">
          <TransactionList transactions={transactions} onDelete={deleteTransaction} onEdit={openEditDialog} />
        </TabsContent>
        <TabsContent value="revenues">
          <TransactionList transactions={revenues} onDelete={deleteTransaction} onEdit={openEditDialog} />
        </TabsContent>
        <TabsContent value="expenses">
          <TransactionList transactions={expenses} onDelete={deleteTransaction} onEdit={openEditDialog} />
        </TabsContent>
      </Tabs>

      {/* ---- Dialog: Configuration ---- */}
      <Dialog open={isConfigOpen} onOpenChange={setIsConfigOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Configuration du Cash</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Solde initial ($)</Label>
              <Input
                type="number"
                value={editConfig.initial_balance}
                onChange={(e) => setEditConfig({ ...editConfig, initial_balance: parseFloat(e.target.value) || 0 })}
                data-testid="config-initial-balance"
              />
            </div>
            <div>
              <Label>Date de début</Label>
              <Input
                type="date"
                value={editConfig.start_date}
                onChange={(e) => setEditConfig({ ...editConfig, start_date: e.target.value })}
                data-testid="config-start-date"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConfigOpen(false)}>Annuler</Button>
            <Button onClick={saveConfig} data-testid="config-save-btn">
              <Save className="w-4 h-4 mr-2" />Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Dialog: Ajouter Transaction ---- */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{newTransaction.id ? "Modifier la transaction cash" : "Ajouter une transaction cash"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Type */}
            <div>
              <Label>Type</Label>
              <Select
                value={newTransaction.type}
                onValueChange={(v) => setNewTransaction({
                  ...DEFAULT_TX,
                  date: newTransaction.date,
                  type: v
                })}
              >
                <SelectTrigger data-testid="tx-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="revenue">
                    <span className="flex items-center gap-2">
                      <ArrowUpCircle className="w-4 h-4 text-emerald-600" /> Revenu
                    </span>
                  </SelectItem>
                  <SelectItem value="expense">
                    <span className="flex items-center gap-2">
                      <ArrowDownCircle className="w-4 h-4 text-red-600" /> Dépense
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date */}
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={newTransaction.date}
                onChange={(e) => setNewTransaction({ ...newTransaction, date: e.target.value })}
                data-testid="tx-date-input"
              />
            </div>

            {/* Montant */}
            <div>
              <Label>Montant ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={newTransaction.amount}
                onChange={(e) => setNewTransaction({ ...newTransaction, amount: e.target.value })}
                placeholder="0.00"
                data-testid="tx-amount-input"
              />
            </div>

            {/* Catégorie */}
            <div>
              <Label>Catégorie</Label>
              <Select
                value={newTransaction.categorie}
                onValueChange={(v) => handleCategoryChange(v, isRevenue)}
              >
                <SelectTrigger data-testid="tx-category-select">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {(isRevenue ? REVENUE_CATEGORIES : EXPENSE_CATEGORIES).map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sous-catégorie immobilisation */}
            {isImmobilisation && (
              <div>
                <Label>Sous-catégorie</Label>
                <Select
                  value={newTransaction.sous_categorie}
                  onValueChange={(v) => setNewTransaction({ ...newTransaction, sous_categorie: v })}
                >
                  <SelectTrigger data-testid="tx-sub-category-select">
                    <SelectValue placeholder="Sélectionner une sous-catégorie..." />
                  </SelectTrigger>
                  <SelectContent>
                    {IMMOBILISATION_SUB_CATEGORIES.map(sub => (
                      <SelectItem key={sub.value} value={sub.value}>{sub.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* --- Salaire / Prime / Bonus classification --- */}
            {isSalaryOrPrime && (
              <div className="p-4 border rounded-lg bg-slate-50 space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Users className="w-4 h-4" />
                  Classification du {newTransaction.categorie === "salaire" ? "salaire" : newTransaction.categorie === "bonus" ? "bonus" : "prime"}
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={newTransaction.salary_type === "terrain" ? "default" : "outline"}
                    className="flex-1"
                    onClick={() => setNewTransaction({ ...newTransaction, salary_type: "terrain", employee_id: "" })}
                    data-testid="salary-terrain-btn"
                  >
                    <HardHat className="w-4 h-4 mr-2" />Terrain
                  </Button>
                  <Button
                    type="button"
                    variant={newTransaction.salary_type === "admin" ? "default" : "outline"}
                    className="flex-1"
                    onClick={() => setNewTransaction({ ...newTransaction, salary_type: "admin" })}
                    data-testid="salary-admin-btn"
                  >
                    <Building2 className="w-4 h-4 mr-2" />Admin
                  </Button>
                </div>

                {/* Admin → sélection employé + ventilation */}
                {isAdminSalary && (
                  <div className="space-y-3 pt-2">
                    {adminEmployees.length > 0 && (
                      <div>
                        <Label className="text-xs">Employé admin</Label>
                        <Select
                          value={newTransaction.employee_id}
                          onValueChange={(v) => {
                            const emp = adminEmployees.find(e => e.id === v);
                            setNewTransaction({
                              ...newTransaction,
                              employee_id: v,
                              description: emp ? `${newTransaction.categorie === "salaire" ? "Salaire" : newTransaction.categorie === "bonus" ? "Bonus" : "Prime"} - ${emp.nom_complet}` : newTransaction.description
                            });
                          }}
                        >
                          <SelectTrigger data-testid="admin-employee-select">
                            <SelectValue placeholder="Sélectionner un employé..." />
                          </SelectTrigger>
                          <SelectContent>
                            {adminEmployees.map(emp => (
                              <SelectItem key={emp.id} value={emp.id}>
                                <span className="flex items-center gap-2">
                                  <User className="w-3 h-3" />
                                  {emp.nom_complet}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-slate-500">Ventilation par département:</p>
                        <span className={`text-xs font-medium ${
                          (newTransaction.admin_ventilation.PRQC + newTransaction.admin_ventilation.LMDS + newTransaction.admin_ventilation.Location) === 100
                            ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          Total: {newTransaction.admin_ventilation.PRQC + newTransaction.admin_ventilation.LMDS + newTransaction.admin_ventilation.Location}%
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        {["PRQC", "LMDS", "Location"].map(dept => (
                          <div key={dept}>
                            <Label className="text-xs">{dept}</Label>
                            <div className="flex items-center gap-1">
                              <Input
                                type="number" min="0" max="100"
                                value={newTransaction.admin_ventilation[dept]}
                                onChange={(e) => setNewTransaction({
                                  ...newTransaction,
                                  admin_ventilation: {
                                    ...newTransaction.admin_ventilation,
                                    [dept]: parseInt(e.target.value) || 0
                                  }
                                })}
                                className="w-16 h-8 text-center"
                                data-testid={`ventilation-${dept.toLowerCase()}`}
                              />
                              <span className="text-sm text-slate-500">%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Terrain → département + sélection employé */}
                {newTransaction.salary_type === "terrain" && (
                  <div className="space-y-3 pt-2">
                    <div>
                      <Label className="text-xs">Département terrain</Label>
                      <Select
                        value={newTransaction.departement}
                        onValueChange={(v) => setNewTransaction({ ...newTransaction, departement: v })}
                      >
                        <SelectTrigger data-testid="terrain-dept-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PRQC">PRQC</SelectItem>
                          <SelectItem value="LMDS">LMDS</SelectItem>
                          <SelectItem value="Location">Location</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {terrainEmployees.length > 0 && (
                      <div>
                        <Label className="text-xs">Employé terrain</Label>
                        <Select
                          value={newTransaction.employee_id}
                          onValueChange={(v) => {
                            const emp = terrainEmployees.find(e => e.id === v);
                            setNewTransaction({
                              ...newTransaction,
                              employee_id: v,
                              description: emp ? `${newTransaction.categorie === "salaire" ? "Salaire" : newTransaction.categorie === "bonus" ? "Bonus" : "Prime"} - ${emp.nom_complet}` : newTransaction.description
                            });
                          }}
                        >
                          <SelectTrigger data-testid="terrain-employee-select">
                            <SelectValue placeholder="Sélectionner un employé..." />
                          </SelectTrigger>
                          <SelectContent>
                            {terrainEmployees.map(emp => (
                              <SelectItem key={emp.id} value={emp.id}>
                                <span className="flex items-center gap-2">
                                  <User className="w-3 h-3" />
                                  {emp.nom_complet}
                                  {/* Cat H — `emp.role` = rôle DE L'EMPLOYÉ dans la liste (donnée), pas RBAC utilisateur. Label affiché dans le dropdown. */}
                                  <span className="text-[10px] text-slate-400">({emp.role === 'chef_equipe' ? 'Chef' : emp.role === 'peintre' ? 'Peintre' : 'Install.'})</span>  // E3b-whitelist : libellé de données
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[10px] text-slate-400 mt-1">
                          Le montant apparaîtra dans la colonne « Bonus » de l'Historique des paies de l'employé.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            <div>
              <Label>Description</Label>
              <Input
                value={newTransaction.description}
                onChange={(e) => setNewTransaction({ ...newTransaction, description: e.target.value })}
                placeholder={isRevenue ? "Ex: Contrat peinture M. Tremblay" : "Ex: Achat matériaux"}
                data-testid="tx-description-input"
              />
            </div>

            {/* Client Name (revenue only) */}
            {isRevenue && (
              <div>
                <Label>Nom du client (optionnel)</Label>
                <ClientAutocomplete
                  value={newTransaction.client_name}
                  onChange={(v) => setNewTransaction({ ...newTransaction, client_name: v })}
                  clients={cashClients}
                />
              </div>
            )}

            {/* Département (auto-rempli pour revenus, éditable pour dépenses non salaire/prime) */}
            {isRevenue && (
              <div>
                <Label>Département <span className="text-xs text-slate-400">(auto selon catégorie)</span></Label>
                <Select
                  value={newTransaction.departement}
                  onValueChange={(v) => setNewTransaction({ ...newTransaction, departement: v })}
                >
                  <SelectTrigger data-testid="tx-dept-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PRQC">PRQC - Peinture Résidentielle</SelectItem>
                    <SelectItem value="LMDS">LMDS - Le Marché du Store</SelectItem>
                    <SelectItem value="Location">Location</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {isExpense && !isSalaryOrPrime && (
              <div>
                <Label>Département</Label>
                <Select
                  value={newTransaction.departement}
                  onValueChange={(v) => setNewTransaction({ ...newTransaction, departement: v })}
                >
                  <SelectTrigger data-testid="tx-dept-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENTS.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsAddOpen(false); setNewTransaction({ ...DEFAULT_TX }); }} data-testid="tx-cancel-btn">Annuler</Button>
            <Button onClick={addTransaction} data-testid="tx-save-btn">
              {newTransaction.id ? (<><Edit3 className="w-4 h-4 mr-2" />Enregistrer</>) : (<><Plus className="w-4 h-4 mr-2" />Ajouter</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 2026-05-12 — Modal de détection / suppression des doublons cash ↔ punchs */}
      <Dialog open={duplicatesModal.open} onOpenChange={(o) => setDuplicatesModal(m => ({ ...m, open: o }))}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>🔍 Doublons cash ↔ paiements cash des punchs</DialogTitle>
            <p className="text-sm text-slate-500 mt-2">
              Les transactions ci-dessous ont la même date, le même employé et un montant identique (±1$) à un paiement cash enregistré directement sur un punch. Elles font donc doubler le total cash de l'entreprise. Sélectionnez celles à supprimer.
            </p>
          </DialogHeader>

          {duplicatesModal.loading ? (
            <div className="flex items-center justify-center py-8 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Détection en cours…
            </div>
          ) : duplicatesModal.duplicates.length === 0 ? (
            <div className="text-center py-8 text-emerald-700">
              <CheckCircle className="w-12 h-12 mx-auto mb-2" />
              <p className="font-medium">Aucun doublon détecté ! Votre comptabilité cash est propre.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="duplicates-list">
              {duplicatesModal.duplicates.map(d => (
                <label
                  key={d.transaction_id}
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition ${duplicatesModal.selected.has(d.transaction_id) ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50'}`}
                  data-testid={`duplicate-${d.transaction_id}`}
                >
                  <input
                    type="checkbox"
                    checked={duplicatesModal.selected.has(d.transaction_id)}
                    onChange={() => toggleDuplicateSelection(d.transaction_id)}
                    className="mt-1"
                    data-testid={`duplicate-checkbox-${d.transaction_id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs bg-slate-100">{d.categorie}</Badge>
                      <span className="text-sm font-medium">{d.description || '(sans description)'}</span>
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      {d.date} · {d.employee_nom || 'Employé non lié'} · <strong>{formatCurrency(d.amount)}</strong>
                      <span className="text-amber-700"> ↔ paiement cash punch : {formatCurrency(d.punch_payment_montant)}</span>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicatesModal({ open: false, duplicates: [], loading: false, selected: new Set() })}>Fermer</Button>
            {duplicatesModal.duplicates.length > 0 && (
              <Button
                onClick={confirmDeleteDuplicates}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-duplicates-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer {duplicatesModal.selected.size} doublon{duplicatesModal.selected.size > 1 ? 's' : ''}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
// ---- Cash Charts Component ----

const CHART_COLORS = ['#0c4a6e', '#0369a1', '#0891b2', '#059669', '#d97706', '#dc2626', '#7c3aed', '#db2777'];
const CATEGORY_LABELS = {
  salaire: "Salaire", prime: "Prime", dividendes: "Dividendes", immobilisation: "Immobilisation",
  contrat_peinture: "Contrat Peinture", contrat_location: "Contrat Location", contrat_lmds: "Contrat LMDS"
};

const CashCharts = ({ breakdown, year }) => {
  const expenseData = Object.entries(breakdown.expense_by_category || {})
    .filter(([, v]) => v > 0)
    .map(([key, val]) => ({ name: CATEGORY_LABELS[key] || key, value: val }));
  const revenueDeptData = Object.entries(breakdown.revenue_by_department || {})
    .filter(([, v]) => v > 0)
    .map(([key, val]) => ({ name: key, value: val }));

  if (expenseData.length === 0 && revenueDeptData.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="cash-charts">
      {expenseData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-red-500" />
              Dépenses par catégorie ({year})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={expenseData} cx="50%" cy="50%" innerRadius={55} outerRadius={90}
                  dataKey="value" nameKey="name" paddingAngle={3}>
                  {expenseData.map((_, idx) => (
                    <Cell key={_.id || _._id || idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
      {revenueDeptData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-emerald-500" />
              Revenus par département ({year})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={revenueDeptData}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Bar dataKey="value" fill="#059669" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ---- Transaction List Component ----
const TransactionList = ({ transactions, onDelete, onEdit }) => {
  if (transactions.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-slate-500">
          <Wallet className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p>Aucune transaction</p>
        </CardContent>
      </Card>
    );
  }

  const getCategoryLabel = (tx) => {
    if (!tx.categorie) return null;
    // Handle immobilisation sub-category
    if (tx.categorie.startsWith("immobilisation:")) {
      const subVal = tx.categorie.split(":")[1];
      const sub = IMMOBILISATION_SUB_CATEGORIES.find(s => s.value === subVal);
      return sub ? `Immob. - ${sub.label}` : "Immobilisation";
    }
    const allCats = [...REVENUE_CATEGORIES, ...EXPENSE_CATEGORIES];
    const cat = allCats.find(c => c.value === tx.categorie);
    return cat ? cat.label : tx.categorie;
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-2">
          {transactions.map((tx) => (
            <div
              key={tx.id}
              className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors"
              data-testid={`transaction-${tx.id}`}
            >
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <div className={`p-2 rounded-lg shrink-0 ${tx.type === "revenue" ? "bg-emerald-100" : "bg-red-100"}`}>
                  {tx.type === "revenue" ? (
                    <ArrowUpCircle className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <ArrowDownCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{tx.description}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="text-sm text-slate-500">{tx.date}</span>
                    {tx.client_name && (
                      <Badge variant="outline" className="text-xs">
                        <User className="w-3 h-3 mr-1" />{tx.client_name}
                      </Badge>
                    )}
                    {tx.categorie && (
                      <Badge variant="outline" className="text-xs bg-slate-50">{getCategoryLabel(tx)}</Badge>
                    )}
                    {tx.salary_type && (
                      <Badge
                        variant="secondary"
                        className={`text-xs ${tx.salary_type === "terrain" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}
                      >
                        {tx.salary_type === "terrain" ? (
                          <><HardHat className="w-3 h-3 mr-1" />Terrain</>
                        ) : (
                          <><Building2 className="w-3 h-3 mr-1" />Admin</>
                        )}
                      </Badge>
                    )}
                    {tx.salary_type === "admin" && tx.admin_ventilation && (
                      <span className="text-xs text-slate-500">
                        ({Object.entries(tx.admin_ventilation)
                          .filter(([, v]) => v > 0)
                          .map(([k, v]) => `${k}: ${v}%`)
                          .join(', ')})
                      </span>
                    )}
                    {(tx.salary_type === "terrain" || !tx.salary_type) && tx.departement && (
                      <Badge variant="secondary" className="text-xs">
                        <Building2 className="w-3 h-3 mr-1" />{tx.departement}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0 ml-4">
                <p className={`text-lg font-bold ${tx.type === "revenue" ? "text-emerald-600" : "text-red-600"}`}>
                  {tx.type === "revenue" ? "+" : "-"}{formatCurrency(tx.amount)}
                </p>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => onEdit && onEdit(tx)}
                  className="text-blue-600 hover:text-blue-800"
                  data-testid={`edit-tx-${tx.id}`}
                  title="Modifier cette transaction"
                >
                  <Edit3 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => onDelete(tx.id)}
                  className="text-red-500 hover:text-red-700"
                  data-testid={`delete-tx-${tx.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default GestionCash;
